import type {
  CacheKeyedOptionSource,
  CreatableOptionSource,
  EnrichableOptionSource,
  HierarchicalOptionSource,
  OptionSource,
  PaginatedOptionSource,
  QueryRequiredOptionSource,
  RichlyPresentedOptionSource,
  ServerFilteredOptionSource,
} from './option-source';

/**
 * The capabilities the Toolbox detects on an adapter by presence. Each flag is
 * `true` only when the adapter carries the matching property/method: each
 * capability is detected purely by its presence on the adapter. Detection never
 * mutates the adapter and never calls a resolve path.
 */
export type DetectedCapabilities = {
  cacheKeyed: boolean;
  creatable: boolean;
  enrichable: boolean;
  hierarchical: boolean;
  paginated: boolean;
  queryRequired: boolean;
  richlyPresented: boolean;
  serverFiltered: boolean;
};

/**
 * The own- or inherited property descriptor for `name`, walking the prototype
 * chain. Used so capability detection can inspect the descriptor WITHOUT reading
 * the property value (which would invoke a getter). Detection must never call
 * into the adapter.
 */
function findDescriptor(
  value: object,
  name: string,
): PropertyDescriptor | undefined {
  let current: object | null = value;
  while (current !== null) {
    const descriptor = Object.getOwnPropertyDescriptor(current, name);
    if (descriptor !== undefined) {
      return descriptor;
    }
    current = Object.getPrototypeOf(current);
  }
  return undefined;
}

/**
 * True when `value` carries a callable named `name`, detected WITHOUT invoking
 * it. A plain method has a `value` that is a function; a getter (which we must
 * not call) has `get`/`set` and no `value`, so it is never mistaken for a
 * method. This keeps detection side-effect-free even if an adapter defines an
 * accessor with the same name.
 */
function hasMethod<TName extends string>(value: object, name: TName): boolean {
  const descriptor = findDescriptor(value, name);
  return descriptor !== undefined && typeof descriptor.value === 'function';
}

/**
 * `source is OptionSource<TItem, TValue> & PaginatedOptionSource<TItem>`: the adapter
 * exposes the incremental `resolvePage` path, so the Toolbox loads it page by
 * page instead of expecting one full list.
 */
export function isPaginatedOptionSource<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
): source is OptionSource<TItem, TValue> & PaginatedOptionSource<TItem> {
  return hasMethod(source, 'resolvePage');
}

/** The adapter exposes `resolveSelection`: a second call enriches the chosen item. */
export function isEnrichableOptionSource<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
): source is OptionSource<TItem, TValue> & EnrichableOptionSource<TItem> {
  return hasMethod(source, 'resolveSelection');
}

/** The adapter exposes `getChildren`: the collection is a lazily-expanded tree. */
export function isHierarchicalOptionSource<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
): source is OptionSource<TItem, TValue> & HierarchicalOptionSource<TItem> {
  return hasMethod(source, 'getChildren');
}

/** The adapter exposes `createOptionFromInput`: free-solo typed values become items. */
export function isCreatableOptionSource<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
): source is OptionSource<TItem, TValue> & CreatableOptionSource<TItem> {
  return hasMethod(source, 'createOptionFromInput');
}

/** The adapter declares `filterMode: 'server'`: client-side filtering is turned off. */
export function isServerFilteredOptionSource<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
): source is OptionSource<TItem, TValue> & ServerFilteredOptionSource {
  const descriptor = findDescriptor(source, 'filterMode');
  return descriptor?.value === 'server';
}

/**
 * The adapter declares BOTH `filterMode: 'server'` AND `queryRequired: true`: the
 * source cannot resolve without a query, so the Toolbox must demand a non-empty
 * query before it calls resolve. Both are read as DATA properties via the
 * descriptor (never a getter), so detection stays side-effect-free even if an
 * adapter defines an accessor of the same name. A `queryRequired` without server
 * filtering is not detected: the two together are the whole capability.
 */
export function isQueryRequiredOptionSource<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
): source is OptionSource<TItem, TValue> & QueryRequiredOptionSource {
  const filterMode = findDescriptor(source, 'filterMode');
  const queryRequired = findDescriptor(source, 'queryRequired');
  return filterMode?.value === 'server' && queryRequired?.value === true;
}

/**
 * The adapter exposes `computeCacheKey`: it owns its cache key and folds the
 * principal identity in, so the shared suite can assert cross-user isolation on
 * the adapter itself.
 */
export function isCacheKeyedOptionSource<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
): source is OptionSource<TItem, TValue> & CacheKeyedOptionSource {
  return hasMethod(source, 'computeCacheKey');
}

/**
 * The adapter exposes at least one rich-presentation accessor (`getOptionColumns`
 * for a table, `getOptionGroup` for a sectioned list).
 */
export function isRichlyPresentedOptionSource<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
): source is OptionSource<TItem, TValue> & RichlyPresentedOptionSource<TItem> {
  return (
    hasMethod(source, 'getOptionColumns') || hasMethod(source, 'getOptionGroup')
  );
}

/**
 * Detects every optional capability on an adapter by presence. The Toolbox uses
 * this to decide how to drive the adapter (paginate or not, enrich or not,
 * server-filter or not) without the adapter having to declare a manifest.
 */
export function detectCapabilities<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
): DetectedCapabilities {
  return {
    cacheKeyed: isCacheKeyedOptionSource(source),
    creatable: isCreatableOptionSource(source),
    enrichable: isEnrichableOptionSource(source),
    hierarchical: isHierarchicalOptionSource(source),
    paginated: isPaginatedOptionSource(source),
    queryRequired: isQueryRequiredOptionSource(source),
    richlyPresented: isRichlyPresentedOptionSource(source),
    serverFiltered: isServerFilteredOptionSource(source),
  };
}
